<template>
  <div>
    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2">Loading performance data...</p>
      </div>
    </div>

    <b-card class="mt-4 shadow-sm border-0 overflow-hidden" no-body>
      <!-- Header -->
      <b-card-header class="bg-gradient-primary text-white py-3">
        <b-row class="align-items-center">
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center">
              <div class="icon-circle bg-white text-primary me-3">
                <i class="ri-bar-chart-line fs-18"></i>
              </div>
              <div>
                <h4 class="card-title text-white mb-0">
                  Performance Analytics Dashboard
                </h4>
                <small class="text-white-50">
                  <i class="ri-user-line me-1"></i>
                  User: {{ userName || 'Loading...' }} | 
                  Last updated: {{ lastUpdated ? formatTime(lastUpdated) : 'Just now' }}
                </small>
              </div>
            </div>
          </b-col>
          <b-col cols="12" md="6" class="text-md-end mt-2 mt-md-0">
            <div class="btn-group" role="group">
              <b-button
                variant="light"
                size="sm"
                class="btn-rounded me-2"
                @click="printReport"
                :disabled="loading"
              >
                <i class="ri-printer-line me-1"></i> Print
              </b-button>
              <b-button
                variant="outline-light"
                size="sm"
                class="btn-rounded"
                @click="exportToExcel"
                :disabled="loading || !kpi || Object.keys(kpi).length === 0"
              >
                <i class="ri-file-excel-line me-1"></i> Export
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-card-header>

      <!-- Filters Section -->
      <b-card-body class="bg-light-subtle border-bottom">
        <b-card class="border-0 shadow-sm">
          <b-card-body class="p-3">
            <h6 class="mb-3 text-primary">
              <i class="ri-filter-line me-2"></i>Filter Performance Data
            </h6>
            <b-form @submit.prevent="loadUserKPI" class="row g-3">
              <b-col md="3">
                <label class="form-label fw-semibold">Status</label>
                <b-form-select
                  v-model="filters.status"
                  :options="statusOptions"
                  class="form-select"
                  :disabled="loading"
                />
              </b-col>
              
              <b-col md="3">
                <label class="form-label fw-semibold">Date Range</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <i class="ri-calendar-line"></i>
                  </span>
                  <b-form-input
                    type="date"
                    v-model="filters.start_date"
                    placeholder="Start Date"
                    :disabled="loading"
                  />
                </div>
              </b-col>
              
              <b-col md="3">
                <label class="form-label fw-semibold">&nbsp;</label>
                <div class="input-group">
                  <span class="input-group-text">
                    <i class="ri-calendar-line"></i>
                  </span>
                  <b-form-input
                    type="date"
                    v-model="filters.end_date"
                    placeholder="End Date"
                    :disabled="loading"
                  />
                </div>
              </b-col>
              
              <b-col md="3" class="d-flex align-items-end">
                <div class="w-100">
                  <b-button
                    type="submit"
                    variant="primary"
                    class="w-100"
                    :disabled="loading"
                  >
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    <i v-else class="ri-refresh-line me-1"></i>
                    {{ loading ? 'Loading...' : 'Apply Filters' }}
                  </b-button>
                </div>
              </b-col>
            </b-form>
            
            <!-- Active Filters -->
            <div v-if="hasActiveFilters" class="mt-3">
              <small class="text-muted me-2">Active filters:</small>
              <b-badge
                v-if="filters.status"
                variant="light"
                class="me-2 mb-1"
                pill
              >
                Status: {{ getStatusLabel(filters.status) }}
                <i class="ri-close-line ms-1" @click="filters.status = null; loadUserKPI()"></i>
              </b-badge>
              <b-badge
                v-if="filters.start_date"
                variant="light"
                class="me-2 mb-1"
                pill
              >
                From: {{ filters.start_date }}
                <i class="ri-close-line ms-1" @click="filters.start_date = null; loadUserKPI()"></i>
              </b-badge>
              <b-badge
                v-if="filters.end_date"
                variant="light"
                class="me-2 mb-1"
                pill
              >
                To: {{ filters.end_date }}
                <i class="ri-close-line ms-1" @click="filters.end_date = null; loadUserKPI()"></i>
              </b-badge>
            </div>
          </b-card-body>
        </b-card>
      </b-card-body>

      <!-- Empty State -->
      <div v-if="!loading && (!kpi || Object.keys(kpi).length === 0)" class="text-center p-5">
        <div class="mb-3">
          <i class="ri-bar-chart-line text-muted" style="font-size: 4rem;"></i>
        </div>
        <h5 class="text-muted">No performance data available</h5>
        <p class="text-muted mb-0">Try adjusting your filters or check back later</p>
      </div>

      <!-- KPI Cards Grid -->
      <div v-else-if="!loading" class="p-4">
        <!-- Summary Row -->
        <b-row class="mb-4">
          <b-col md="3" sm="6" class="mb-3">
            <b-card class="border-0 shadow-sm h-100 bg-primary-subtle">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0">
                  <div class="avatar-sm rounded-circle bg-primary text-white d-flex align-items-center justify-content-center">
                    <i class="ri-task-line fs-18"></i>
                  </div>
                </div>
                <div class="flex-grow-1 ms-3">
                  <h3 class="mb-1">{{ kpi.total_activities || 0 }}</h3>
                  <p class="text-muted mb-0">Total Activities</p>
                </div>
              </div>
            </b-card>
          </b-col>
          
          <b-col md="3" sm="6" class="mb-3">
            <b-card class="border-0 shadow-sm h-100 bg-success-subtle">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0">
                  <div class="avatar-sm rounded-circle bg-success text-white d-flex align-items-center justify-content-center">
                    <i class="ri-checkbox-circle-line fs-18"></i>
                  </div>
                </div>
                <div class="flex-grow-1 ms-3">
                  <h3 class="mb-1">{{ kpi.completed_activities || 0 }}</h3>
                  <p class="text-muted mb-0">Completed</p>
                </div>
              </div>
            </b-card>
          </b-col>
          
          <b-col md="3" sm="6" class="mb-3">
            <b-card class="border-0 shadow-sm h-100 bg-warning-subtle">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0">
                  <div class="avatar-sm rounded-circle bg-warning text-white d-flex align-items-center justify-content-center">
                    <i class="ri-time-line fs-18"></i>
                  </div>
                </div>
                <div class="flex-grow-1 ms-3">
                  <h3 class="mb-1">{{ kpi.pending_activities || 0 }}</h3>
                  <p class="text-muted mb-0">Pending</p>
                </div>
              </div>
            </b-card>
          </b-col>
          
          <b-col md="3" sm="6" class="mb-3">
            <b-card class="border-0 shadow-sm h-100 bg-danger-subtle">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0">
                  <div class="avatar-sm rounded-circle bg-danger text-white d-flex align-items-center justify-content-center">
                    <i class="ri-alarm-warning-line fs-18"></i>
                  </div>
                </div>
                <div class="flex-grow-1 ms-3">
                  <h3 class="mb-1">{{ kpi.overdue_activities || 0 }}</h3>
                  <p class="text-muted mb-0">Overdue</p>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <!-- Detailed KPIs -->
        <b-row class="g-4">
          <!-- Activities Overview -->
          <b-col lg="6" xl="4">
            <b-card class="h-100 shadow-sm border-0">
              <b-card-header class="bg-transparent border-bottom py-3">
                <div class="d-flex align-items-center">
                  <div class="icon-circle bg-warning text-dark me-3">
                    <i class="ri-task-line"></i>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-0">Activities Overview</h6>
                    <small class="text-muted">Performance breakdown</small>
                  </div>
                  <div>
                    <b-badge :variant="getCompletionVariant" pill>
                      {{ activityCompletionRate }}%
                    </b-badge>
                  </div>
                </div>
              </b-card-header>
              <b-card-body class="p-3">
                <div class="mb-3">
                  <div class="d-flex justify-content-between mb-1">
                    <span class="text-muted">Completion Rate</span>
                    <span class="fw-semibold">{{ activityCompletionRate }}%</span>
                  </div>
                  <b-progress
                    :value="activityCompletionRate"
                    height="6px"
                    :variant="getCompletionVariant"
                    class="mb-3"
                  />
                </div>
                
                <div class="kpi-stats">
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Total Activities</span>
                    <span class="fw-semibold">{{ kpi.total_activities || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Completed</span>
                    <span class="fw-semibold text-success">{{ kpi.completed_activities || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Pending</span>
                    <span class="fw-semibold text-warning">{{ kpi.pending_activities || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center">
                    <span class="text-muted">Overdue</span>
                    <span class="fw-semibold text-danger">{{ kpi.overdue_activities || 0 }}</span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Timeliness Metrics -->
          <b-col lg="6" xl="4">
            <b-card class="h-100 shadow-sm border-0">
              <b-card-header class="bg-transparent border-bottom py-3">
                <div class="d-flex align-items-center">
                  <div class="icon-circle bg-info text-white me-3">
                    <i class="ri-time-line"></i>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-0">Timeliness Metrics</h6>
                    <small class="text-muted">Delivery performance</small>
                  </div>
                </div>
              </b-card-header>
              <b-card-body class="p-3">
                <div class="kpi-stats">
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">On-Time Activities</span>
                    <span class="fw-semibold">{{ kpi.ontime_activities || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Average Delay</span>
                    <span :class="{'text-danger': kpi.avg_delay > 0, 'text-success': kpi.avg_delay <= 0}">
                      {{ kpi.avg_delay || 0 }} days
                    </span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Late Completion Rate</span>
                    <span class="fw-semibold">{{ kpi.late_completion_rate || 0 }}%</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Upcoming (7 days)</span>
                    <span class="fw-semibold">{{ kpi.upcoming_activities || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center">
                    <span class="text-muted">Timeliness Score</span>
                    <b-badge :variant="getTimelinessVariant" pill>
                      {{ kpi.timeliness_score || 0 }}%
                    </b-badge>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Performance Metrics -->
          <b-col lg="6" xl="4">
            <b-card class="h-100 shadow-sm border-0">
              <b-card-header class="bg-transparent border-bottom py-3">
                <div class="d-flex align-items-center">
                  <div class="icon-circle bg-success text-white me-3">
                    <i class="ri-bar-chart-box-line"></i>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-0">Performance Metrics</h6>
                    <small class="text-muted">Quality & impact</small>
                  </div>
                </div>
              </b-card-header>
              <b-card-body class="p-3">
                <div class="kpi-stats">
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Impact Score</span>
                    <b-badge :variant="getImpactVariant" pill>
                      {{ kpi.impact_score || 0 }}%
                    </b-badge>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Avg Completion Time</span>
                    <span class="fw-semibold">{{ kpi.avg_completion_time || 0 }} days</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">High Priority</span>
                    <span class="fw-semibold text-danger">{{ kpi.high_priority_activities || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Blocked Activities</span>
                    <span class="fw-semibold">{{ kpi.blocked_activities || 0 }}</span>
                  </div>
                 
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Projects & Workload -->
          <b-col lg="6" xl="4">
            <b-card class="h-100 shadow-sm border-0">
              <b-card-header class="bg-transparent border-bottom py-3">
                <div class="d-flex align-items-center">
                  <div class="icon-circle bg-primary text-white me-3">
                    <i class="ri-folder-3-line"></i>
                  </div>
                  <div class="flex-grow-1">
                    <h6 class="mb-0">Projects & Workload</h6>
                    <small class="text-muted">Distribution & capacity</small>
                  </div>
                </div>
              </b-card-header>
              <b-card-body class="p-3">
                <div class="kpi-stats">
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Projects Assigned</span>
                    <span class="fw-semibold">{{ kpi.total_projects || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Avg Activities/Project</span>
                    <span class="fw-semibold">{{ kpi.avg_activities_per_project || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Deleted Activities</span>
                    <span class="fw-semibold">{{ kpi.deleted_activities || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted">Workload Score</span>
                    <span :class="getWorkloadClass">{{ kpi.workload_score || 0 }}</span>
                  </div>
                  <div class="kpi-stat-item d-flex justify-content-between align-items-center">
                    <span class="text-muted">Active Projects</span>
                    <span class="fw-semibold text-success">{{ kpi.active_projects || 0 }}</span>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Performance Insights -->
    <b-card v-if="!loading && kpi && Object.keys(kpi).length > 0" class="mt-4 shadow-sm border-0">
      <b-card-header class="bg-transparent border-bottom py-3">
        <h6 class="mb-0 text-primary">
          <i class="ri-lightbulb-line me-2"></i>Performance Insights
        </h6>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col md="6" class="mb-3">
            <div class="p-3 bg-success-subtle rounded-3">
              <h6 class="text-success mb-2">Strengths</h6>
              <ul class="mb-0 ps-3">
                <li v-if="activityCompletionRate >= 80">
                  High activity completion rate ({{ activityCompletionRate }}%)
                </li>
                <li v-if="kpi.ontime_activities > (kpi.total_activities * 0.7)">
                  Strong on-time delivery performance
                </li>
                <li v-if="kpi.impact_score >= 80">
                  Excellent impact score on deliverables
                </li>
                <li v-else>Consistent performance across multiple projects</li>
              </ul>
            </div>
          </b-col>
          <b-col md="6" class="mb-3">
            <div class="p-3 bg-danger-subtle rounded-3">
              <h6 class="text-danger mb-2">Areas for Improvement</h6>
              <ul class="mb-0 ps-3">
                <li v-if="kpi.overdue_activities > 0">
                  {{ kpi.overdue_activities }} overdue activities need attention
                </li>
                <li v-if="kpi.avg_delay > 2">
                  Average delay of {{ kpi.avg_delay }} days on activities
                </li>
                <li v-if="kpi.late_completion_rate > 30">
                  High late completion rate ({{ kpi.late_completion_rate }}%)
                </li>
                <li v-else>Monitor pending activities for timely completion</li>
              </ul>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import reportService from '../../../services/report.service';

export default {
  name: 'UserPerformanceDashboard',
  data() {
    return {
      kpi: {},
      loading: false,
      lastUpdated: null,
      userName: '',
      filters: {
        status: null,
        start_date: null,
        end_date: null,
      },
      statusOptions: [
        { value: null, text: 'All Statuses' },
        { value: 'completed', text: 'Completed' },
        { value: 'pending', text: 'Pending' },
        { value: 'in-progress', text: 'In Progress' },
        { value: 'blocked', text: 'Blocked' },
      ],
    };
  },
  computed: {
    activityCompletionRate() {
      if (!this.kpi.total_activities || this.kpi.total_activities === 0) return 0;
      return Math.round((this.kpi.completed_activities / this.kpi.total_activities) * 100);
    },
    getCompletionVariant() {
      if (this.activityCompletionRate >= 80) return 'success';
      if (this.activityCompletionRate >= 60) return 'warning';
      return 'danger';
    },
    getTimelinessVariant() {
      const score = this.kpi.timeliness_score || 0;
      if (score >= 80) return 'success';
      if (score >= 60) return 'warning';
      return 'danger';
    },
    getImpactVariant() {
      const score = this.kpi.impact_score || 0;
      if (score >= 80) return 'success';
      if (score >= 60) return 'warning';
      return 'danger';
    },
    getWorkloadClass() {
      const score = this.kpi.workload_score || 0;
      if (score <= 3) return 'text-success fw-semibold';
      if (score <= 6) return 'text-warning fw-semibold';
      return 'text-danger fw-semibold';
    },
    hasActiveFilters() {
      return this.filters.status || this.filters.start_date || this.filters.end_date;
    },
  },
  methods: {
    async loadUserKPI() {
      this.loading = true;
      try {
        const response = await reportService.getUserKPI(this.$route.params.id, this.filters);
        if (response.success) {
          this.kpi = response.activityStatistics || {};
          this.userName = response.user_name || 'User';
          this.lastUpdated = new Date();
          
          // Show success message
          this.$bvToast.toast('Performance data loaded successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          });
        } else {
          throw new Error(response.message || 'Failed to load data');
        }
      } catch (error) {
        console.error('Failed to fetch user KPI:', error);
        this.$bvToast.toast(error.message || 'Failed to load performance data', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },

    getStatusLabel(status) {
      const option = this.statusOptions.find(opt => opt.value === status);
      return option ? option.text : status;
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    printReport() {
      window.print();
    },

    exportToExcel() {
      if (!this.kpi || Object.keys(this.kpi).length === 0) return;
      
      const data = [
        ['Performance Metrics Report', '', '', ''],
        ['Generated:', new Date().toLocaleString(), '', ''],
        ['User:', this.userName, '', ''],
        ['', '', '', ''],
        ['Metric', 'Value', 'Status', 'Notes'],
        ['Total Activities', this.kpi.total_activities || 0, '', ''],
        ['Completed Activities', this.kpi.completed_activities || 0, `${this.activityCompletionRate}%`, ''],
        ['Pending Activities', this.kpi.pending_activities || 0, '', ''],
        ['Overdue Activities', this.kpi.overdue_activities || 0, 'Needs Attention', ''],
        ['On-Time Activities', this.kpi.ontime_activities || 0, '', ''],
        ['Average Delay', this.kpi.avg_delay || 0, 'days', ''],
        ['Impact Score', this.kpi.impact_score || 0, '%', ''],
        ['Workload Score', this.kpi.workload_score || 0, '', '']
      ];

      let csvContent = "data:text/csv;charset=utf-8,";
      data.forEach(row => {
        csvContent += row.join(",") + "\r\n";
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `performance_report_${this.userName}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
  mounted() {
    this.loadUserKPI();
  },
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* background-color: rgba(255, 255, 255, 0.85); */
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.spinner-container {
  text-align: center;
  background: var(--bs-body-bg,);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--bs-border-color, #dee2e6);
}

.spinner-container .spinner-border {
  width: 3rem;
  height: 3rem;
  color: var(--bs-primary, #3b82f6);
}

.spinner-container p {
  color: var(--bs-body-color, #495057);
  font-weight: 500;
  margin-top: 1rem;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 18px;
}

.kpi-stats .kpi-stat-item {
  padding: 0.5rem 0;
  border-bottom: 1px dashed var(--bs-border-color);
}

.kpi-stats .kpi-stat-item:last-child {
  border-bottom: none;
}

.avatar-sm {
  width: 48px;
  height: 48px;
}

/* Print styles */
@media print {
  .btn-group,
  .bg-gradient-primary .text-md-end,
  .filters-section {
    display: none !important;
  }
  
  .card {
    border: none !important;
    box-shadow: none !important;
  }
}

/* Dark mode support */
[data-bs-theme="dark"] .loading-overlay {
  background-color: rgba(33, 37, 41, 0.85);
}

[data-bs-theme="dark"] .spinner-container {
  background: #212529;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  border: 1px solid #495057;
}

[data-bs-theme="dark"] .spinner-container p {
  color: #e9ecef;
}

[data-bs-theme="dark"] .bg-light-subtle {
  background-color: #2d3748 !important;
}
</style>